/**
 * Financial Reports Page
 * Comprehensive financial reporting with export capabilities
 */

import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';

type ReportType = 'income' | 'invoices' | 'transactions' | 'tax' | 'products';

export const FinancialReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportType>('income');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState<any>(null);

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadReport();
  }, [activeTab, dateRange]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate).toISOString(),
      });

      const response = await adminApi.get(`/financial-reports/${activeTab}?${params.toString()}`);
      setReportData(response.data.report);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate).toISOString(),
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/financial-reports/${activeTab}/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Failed to export report: ' + err.message);
    }
  };

  const tabs = [
    { id: 'income', label: 'Income Report', icon: '💰' },
    { id: 'invoices', label: 'Invoice Report', icon: '📄' },
    { id: 'transactions', label: 'Transaction Report', icon: '💳' },
    { id: 'tax', label: 'Tax Report', icon: '🧾' },
    { id: 'products', label: 'Product Revenue', icon: '📦' },
  ];

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Financial Reports</h1>
            <p className="text-slate-400 mt-1">Comprehensive financial analysis and reporting</p>
          </div>
          <button
            onClick={handleExport}
            disabled={loading || !reportData}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <span>Export CSV</span>
          </button>
        </div>

        {/* Date Range Controls */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700/50">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-300">From:</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
                }
                className="px-3 py-1.5 bg-slate-700/50 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-300">To:</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-1.5 bg-slate-700/50 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 mb-6">
          <div className="flex border-b border-slate-700/50">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ReportType)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-700/50 text-white border-b-2 border-orange-500'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50/50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Report Content */}
          <div className="p-6">
            {error && (
              <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : reportData ? (
              <>
                {activeTab === 'income' && <IncomeReportView data={reportData} />}
                {activeTab === 'invoices' && <InvoiceReportView data={reportData} />}
                {activeTab === 'transactions' && <TransactionReportView data={reportData} />}
                {activeTab === 'tax' && <TaxReportView data={reportData} />}
                {activeTab === 'products' && <ProductRevenueReportView data={reportData} />}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

// Income Report View Component
const IncomeReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary Cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-lg p-4">
        <p className="text-sm text-slate-400 uppercase">Total Revenue</p>
        <p className="text-2xl font-bold text-white mt-2">${data.summary.totalRevenue.toFixed(2)}</p>
        <p className="text-xs text-slate-400 mt-1">{data.summary.totalInvoices} invoices</p>
      </div>

      <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-orange-500/30 rounded-lg p-4">
        <p className="text-sm text-slate-400 uppercase">Net Revenue</p>
        <p className="text-2xl font-bold text-white mt-2">${data.summary.netRevenue.toFixed(2)}</p>
        <p className="text-xs text-slate-400 mt-1">After ${data.summary.totalRefunds.toFixed(2)} refunds</p>
      </div>

      <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-lg p-4">
        <p className="text-sm text-slate-400 uppercase">Avg Invoice Value</p>
        <p className="text-2xl font-bold text-white mt-2">${data.summary.averageInvoiceValue.toFixed(2)}</p>
      </div>
    </div>

    {/* Revenue by Product */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Revenue by Product</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Product</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Revenue</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Invoices</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.byProduct.map((product: any) => (
              <tr key={product.productId} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white">{product.productName}</td>
                <td className="px-4 py-2 text-sm text-right text-green-400 font-medium">${product.revenue.toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{product.invoiceCount}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{product.percentageOfTotal.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Revenue by Gateway */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Revenue by Payment Gateway</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Gateway</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Revenue</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Transactions</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.byGateway.map((gateway: any, index: number) => (
              <tr key={index} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white">{gateway.gateway}</td>
                <td className="px-4 py-2 text-sm text-right text-green-400 font-medium">${gateway.revenue.toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{gateway.transactionCount}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{gateway.percentageOfTotal.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Invoice Report View Component
const InvoiceReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary Grid */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Invoices</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.totalInvoices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Paid</p>
        <p className="text-xl font-bold text-green-400 mt-1">{data.summary.paidInvoices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Unpaid</p>
        <p className="text-xl font-bold text-yellow-400 mt-1">{data.summary.unpaidInvoices}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Overdue</p>
        <p className="text-xl font-bold text-red-400 mt-1">{data.summary.overdueInvoices}</p>
      </div>
    </div>

    {/* Status Breakdown */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Status Breakdown</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Count</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Value</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">% Count</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">% Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.statusBreakdown.map((status: any) => (
              <tr key={status.status} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white capitalize">{status.status}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{status.count}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">${status.totalValue.toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{status.percentageOfCount.toFixed(1)}%</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{status.percentageOfValue.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Transaction Report View Component
const TransactionReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Transactions</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.totalTransactions}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Success Rate</p>
        <p className="text-xl font-bold text-green-400 mt-1">{data.summary.successRate.toFixed(1)}%</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Volume</p>
        <p className="text-xl font-bold text-white mt-1">${data.summary.totalVolume.toFixed(2)}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Avg Value</p>
        <p className="text-xl font-bold text-white mt-1">${data.summary.averageTransactionValue.toFixed(2)}</p>
      </div>
    </div>

    {/* By Gateway */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Transactions by Gateway</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Gateway</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Total</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Success</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Failed</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Volume</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Success Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.byGateway.map((gateway: any) => (
              <tr key={gateway.gateway} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white">{gateway.gateway}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{gateway.totalTransactions}</td>
                <td className="px-4 py-2 text-sm text-right text-green-400">{gateway.successfulTransactions}</td>
                <td className="px-4 py-2 text-sm text-right text-red-400">{gateway.failedTransactions}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">${gateway.totalVolume.toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{gateway.successRate.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Tax Report View Component
const TaxReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Tax Collected</p>
        <p className="text-xl font-bold text-white mt-1">${data.summary.totalTaxCollected.toFixed(2)}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Taxable Amount</p>
        <p className="text-xl font-bold text-white mt-1">${data.summary.totalTaxableAmount.toFixed(2)}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Average Tax Rate</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.averageTaxRate.toFixed(2)}%</p>
      </div>
    </div>
  </div>
);

// Product Revenue Report View Component
const ProductRevenueReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-6">
    {/* Summary */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Products</p>
        <p className="text-xl font-bold text-white mt-1">{data.summary.totalProducts}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Total Revenue</p>
        <p className="text-xl font-bold text-white mt-1">${data.summary.totalRevenue.toFixed(2)}</p>
      </div>
      <div className="bg-slate-700/50/30 rounded-lg p-4">
        <p className="text-xs text-slate-400 uppercase">Avg Revenue Per Product</p>
        <p className="text-xl font-bold text-white mt-1">${data.summary.averageRevenuePerProduct.toFixed(2)}</p>
      </div>
    </div>

    {/* Product Table */}
    <div className="bg-slate-700/50/30 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Revenue by Product</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700/50/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Product</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-300 uppercase">Type</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Revenue</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Orders</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Services</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">Avg Value</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-slate-300 uppercase">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {data.byProduct.map((product: any) => (
              <tr key={product.productId} className="hover:bg-slate-700/50/30">
                <td className="px-4 py-2 text-sm text-white">{product.productName}</td>
                <td className="px-4 py-2 text-sm text-slate-300 capitalize">{product.productType}</td>
                <td className="px-4 py-2 text-sm text-right text-green-400 font-medium">${product.totalRevenue.toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{product.orderCount}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{product.activeServices}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">${product.averageOrderValue.toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-right text-slate-300">{product.percentageOfTotal.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export default FinancialReportsPage;
